import { useState } from 'react';
import { drillForPanel, whyFor } from './pipelineDrill';
import type { Drill } from './drill';
import { Badge, InfoHint } from '../../components/ui';
import { FunnelRow } from './FunnelRow';
import { buildPipeline, pipelineSentence } from './leadPipeline';
import type { PipelineRow } from './leadPipeline';
import {
  FUNNEL_EXPLAINERS,
  allRows,
  buildFunnel,
  lossReasons,
  neverEmailedRows,
  sharePercent,
} from './pipelineFunnel';
import type { FunnelStep } from './pipelineFunnel';
import { ACTION_EXPLAINERS, ATTENTION_EXPLAINERS, buildActions } from './pipelineActions';
import type { ActionKey, PlannedItem } from './pipelineActions';
import { ATTENTION_RULE_EXPLAINERS, buildAttention } from './pipelineAttention';
import type { AttentionKey } from './pipelineAttention';
import type { LeadLoop } from './leadLoop';
import type { LeadFollowup } from './leadFollowups';
import type { RunningRound } from './leadRounds';
import type { TapPerson } from '../projects/types';
import type { ActivitySlim, AdminActivitySlim, ProspectActivitySlim } from './activityStats';
import type { LeadDealOutcome } from './dealOutcomes';
import { originBreakdown, originTotal } from './originBreakdown';
import {
  DEFAULT_PIPELINE_RANGE,
  PIPELINE_RANGE_LABELS,
  PIPELINE_RANGE_ORDER,
  leadsInRange,
  pipelineRangeBounds,
  pipelineRangeNote,
} from './pipelineRange';
import type { PipelineRangeKey } from './pipelineRange';
import type { OriginRow } from './originBreakdown';
import type { LeadClosureReason, LeadSource } from './leadFields';
import { repliedLeadIds } from './activityStats';
import { dayLabel, todayIso } from './trackerFormat';
import type { Lead, LeadStage, Membership } from './types';

/**
 * The Pipeline: one slice of leads, and what has happened to them.
 *
 * ── The moment this exists for ─────────────────────────────────────────────
 *
 * Kyu, 31 Aug, after a huddle: Alex asked how the LinkedIn ad leads were
 * doing — how many replied, how many are in deals — and the Hub could not
 * answer. Every fact was already recorded. There was no screen that put them
 * in one place.
 *
 * ── It records nothing ─────────────────────────────────────────────────────
 *
 * Kyu: "The pipeline doesn't change anything. It actually reflects what's
 * already in there." No new field, no new tagging burden, and no migration
 * behind this rework either.
 *
 * ── Drop A: one summary, one list, never two ───────────────────────────────
 *
 * Kyu approved the rework on 7 September. What came off the page:
 *
 *   1. The nine-column by-origin table. Not deleted — moved into the panel,
 *      opened by "Compare origins" beside the lane control. It answered a
 *      question people ask occasionally and it charged them nine columns of
 *      permanent screen for it.
 *   2. The seven stacked band tables. They became the funnel. Click a step
 *      and that list opens below.
 *   3. The days-waiting column. Noise on every row. The case it existed for —
 *      a lead sitting untouched — is the "no outreach logged" line, which says its
 *      own number and opens its own list.
 *   4. The duplicate range control. One window for the page.
 *
 * ── The bands could not be windowed. The funnel can ────────────────────────
 *
 * This is the part worth knowing, because it removes an apology the page used
 * to print. A lead's row records when it ARRIVED, never when it entered a
 * status, and `lead_stage_log` stores the status's display name across a
 * vocabulary rename with no key to join on. So "how many are sitting in
 * Contacted" was a now-number, the range control applied to the by-origin
 * table alone, and a printed line told the reader the bands below answered a
 * different question.
 *
 * Every step of the funnel is a property of the LEAD — arrived, has a touch,
 * has a reply, reached a deal. So the window applies to all of it, and the
 * apology goes with the bands.
 *
 * ── The panel never appends ────────────────────────────────────────────────
 *
 * There is exactly one panel and clicking a different thing REPLACES it. That
 * is the whole defence against this becoming another screen nobody reads: the
 * page cannot grow, however much somebody clicks.
 *
 * ── The slice is the Leads tab's slice ─────────────────────────────────────
 *
 * `leads` arrives already filtered by the page, using the same filter the
 * Leads table uses. Two filter implementations would be two chances to
 * disagree, and a pipeline that disagrees with the list it links to is worse
 * than no pipeline.
 */

/**
 * What the one panel is currently holding.
 *
 * A union rather than a string, because "the lost leads" and "the leads lost
 * for no budget" are different lists and a single key could not tell them
 * apart without string-splitting a reason somebody typed.
 */
type Selection =
  | { kind: 'step'; step: FunnelStep }
  | { kind: 'action'; action: ActionKey }
  | { kind: 'due' }
  | { kind: 'overdue' }
  | { kind: 'attention'; rule: AttentionKey }
  | { kind: 'never' }
  | { kind: 'reason'; reason: string }
  | { kind: 'origins' };

export function PipelineView({
  leads,
  stages,
  activities,
  prospecting = [],
  admin = [],
  memberships,
  outcomes,
  origins,
  closureReasons,
  followups,
  running,
  loops,
  people,
  sliceLabel,
  onOpenLead,
  onDrill,
  onPickOrigin,
}: {
  /** Already filtered. See the note above — this is deliberate. */
  leads: Lead[];
  stages: LeadStage[];
  activities: ActivitySlim[];
  /** Activity on prospects — the chase. Counted in Done, never in the funnel. */
  prospecting?: ProspectActivitySlim[];
  /** "Updated HubSpot" ticks on deals. Its own line. */
  admin?: AdminActivitySlim[];
  memberships: Membership[];
  /**
   * The deal rollup. Won and Lost are read off it, exactly as the Leads tab
   * reads them — the two screens share one function so they cannot describe
   * the same lead differently.
   */
  outcomes: ReadonlyMap<string, LeadDealOutcome>;
  /** The Lead Origin vocabulary, for the origin comparison. */
  origins: LeadSource[];
  /** The closing-reason vocabulary, so a closed row can say why. */
  closureReasons: LeadClosureReason[];
  /**
   * Open lead follow-ups — ordinary `tasks` rows, not `sales_tasks`.
   *
   * The forward half of the actions funnel. Kyu: "we use Task to remind us
   * when to send email (or any actions needed) on a lead." There is no
   * scheduled-email feature in the Hub and this is not one.
   */
  followups: LeadFollowup[];
  /** Running rounds by lead, for the touches still owed. */
  running: ReadonlyMap<string, RunningRound>;
  /** Each lead's round history, for "round closed, no answer". */
  loops: ReadonlyMap<string, LeadLoop>;
  /** For putting a name on the person who logged a touch. */
  people: TapPerson[];
  /** What the current slice is called, for the sentence. */
  sliceLabel: string;
  onOpenLead: (leadId: string) => void;
  /**
   * Picking an origin narrows the whole page to it, by setting the same
   * filter the Lead Origin dropdown sets. The comparison table is what stops
   * being a dead end — you read the row, then click it to see who is in it.
   */
  /**
   * Somebody clicked a number and wants the records. The page that lists that
   * kind of record takes it from here.
   *
   * Optional so the view still renders in a test harness that does not care.
   */
  onDrill?: (drill: Drill) => void;
  onPickOrigin: (group: string | null, originId: string | null) => void;
}) {
  const today = todayIso();

  // ── The one window control on the page ──────────────────────────────────
  //
  // It used to sit inside the by-origin table's header and range that table
  // alone. Now it ranges everything below it, which is what somebody pressing
  // it always assumed it did.
  const [range, setRange] = useState<PipelineRangeKey>(DEFAULT_PIPELINE_RANGE);
  const bounds = pipelineRangeBounds(range, today);
  const rangedLeads = leadsInRange(leads, bounds);
  const rangeNote = pipelineRangeNote(range, bounds);

  const summary = buildPipeline(
    rangedLeads,
    stages,
    activities,
    memberships,
    today,
    outcomes,
    closureReasons,
  );

  const [selection, setSelection] = useState<Selection>({ kind: 'step', step: 'arrived' });
  const [copied, setCopied] = useState(false);

  const funnel = buildFunnel(summary);
  const never = neverEmailedRows(summary);
  const reasons = lossReasons(summary);
  const sentence = pipelineSentence(summary, sliceLabel);

  const actions = buildActions({
    summary,
    activities,
    prospecting,
    admin,
    followups,
    running,
    bounds,
    today,
    peopleNames: new Map(
      people.map((person) => [
        person.id,
        person.display_name.trim() === '' ? person.email : person.display_name,
      ]),
    ),
  });

  const attention = buildAttention({ summary, running, loops, followups, today });

  /**
   * Lead id to its row, so the actions funnel can hand the panel the same
   * shape the outcome funnel does.
   *
   * The actions side counts touches and tasks, which are not leads — so it
   * carries lead IDS and this resolves them, rather than every count carrying
   * a duplicate copy of the row it points at.
   */
  const rowById = new Map(allRows(summary).map((row) => [row.leadId, row]));

  if (summary.arrived === 0) {
    // Not an empty funnel with labels. "No leads match" is the answer, and a
    // column of zeroes above nothing makes somebody look for a scrollbar.
    return (
      <div className="pipeline">
        <div className="pipeline-head">
          <RangeSwitch range={range} onChange={setRange} />
        </div>
        <p className="tracker-field-note">
          No leads match this slice{rangeNote === '' ? '' : ` in ${PIPELINE_RANGE_LABELS[range]}`}.
          Widen the filters above to see a pipeline.
        </p>
      </div>
    );
  }

  const byOrigin = originBreakdown(
    rangedLeads,
    origins,
    stages,
    outcomes,
    repliedLeadIds([...activities], [...memberships]),
  );
  const total = originTotal(byOrigin, 'Everyone in this slice');

  const panel = panelFor(selection, funnel, never, summary.arrived, actions, attention, rowById);

  /**
   * Select, and open the page if there is one.
   *
   * Both, not either. Selecting keeps the inline panel correct for the
   * selections that have no page — planned work, due today, overdue — and
   * means the Pipeline still shows what you picked when you come back to it.
   *
   * The drill is taken from `panelFor`'s own output rather than resolved again
   * here. Two functions mapping the same selection to the same leads is two
   * definitions of "Emailed", and pipelineFunnel.ts exists so there is one.
   */
  function pick(next: Selection, why = ''): void {
    setSelection(next);
    if (onDrill === undefined) return;
    const resolved = panelFor(next, funnel, never, summary.arrived, actions, attention, rowById);
    // Joined from the parts that are actually set. `rangeNote` is empty on All
    // time, and interpolating it anyway produced "Emailed · leads, all sources
    // · · 461 leads" on the live page.
    const slice = [sliceLabel, rangeNote].map((part) => part.trim()).filter((part) => part !== '');
    const drill = drillForPanel(resolved, slice.join(' · '), why);
    if (drill !== null) {
      onDrill(drill);
    }
  }

  return (
    <div className="pipeline">
      <div className="pipeline-head">
        <p className="pipeline-slice">
          {String(summary.arrived)} {sliceLabel}
          <button
            type="button"
            className="pipeline-copy"
            onClick={() => {
              void navigator.clipboard?.writeText(sentence).then(
                () => {
                  setCopied(true);
                  setTimeout(() => {
                    setCopied(false);
                  }, 2000);
                },
                () => {
                  // Clipboard refused — say so rather than showing "Copied"
                  // for something that was not.
                  setCopied(false);
                },
              );
            }}
          >
            {copied ? 'Copied' : 'Copy the summary'}
          </button>
        </p>
        <div className="pipeline-head-controls">
          {byOrigin.length === 0 ? null : (
            <button
              type="button"
              className={`pipeline-compare${selection.kind === 'origins' ? ' pipeline-compare--on' : ''}`}
              aria-pressed={selection.kind === 'origins'}
              onClick={() => {
                setSelection({ kind: 'origins' });
              }}
            >
              Compare origins
            </button>
          )}
          <RangeSwitch range={range} onChange={setRange} />
        </div>
      </div>
      {rangeNote === '' ? null : <p className="pipe-origins-note">{rangeNote}</p>}

      <section className="pipeline-funnels">
        <article className="pipeline-card">
          <header className="pipeline-card-head">
            <h3>Outcome</h3>
            <p>What happened to these people.</p>
          </header>
          <ul className="pipeline-funnel">
            {funnel.map((line) => (
              <li key={line.key}>
                <FunnelRow
                  label={line.label}
                  explainer={FUNNEL_EXPLAINERS[line.key]}
                  count={line.count}
                  percent={line.percent}
                  tone={line.key === 'won' ? 'won' : line.key === 'lost' ? 'lost' : 'plain'}
                  on={selection.kind === 'step' && selection.step === line.key}
                  onPick={() => {
                    pick({ kind: 'step', step: line.key }, whyFor(line.key));
                  }}
                />
              </li>
            ))}
          </ul>

          {/* The one figure that asks somebody to do something today, and the
              replacement for the days-waiting column. It says its own number
              and opens its own list, which the column never did. */}
          {never.length === 0 ? null : (
            <button
              type="button"
              className={`pipeline-never${selection.kind === 'never' ? ' pipeline-never--on' : ''}`}
              aria-pressed={selection.kind === 'never'}
              onClick={() => {
                pick({ kind: 'never' });
              }}
            >
              {/* "No outreach logged", not "nobody has emailed them". Hanneza,
                  8 September: Brenden Cohen was on this list with June and
                  August emails from Alex visible on his Emails tab. Both were
                  true. The Emails tab reads Gmail live; this line reads what
                  the Hub has LOGGED, and the mail sync only started recording
                  in September and looks back ten days. The old sentence
                  claimed more than the Hub knows. */}
              <b>{String(never.length)} arrived with no outreach logged.</b>
              <InfoHint
                label="No outreach logged"
                text="Leads with no email, text, call, LinkedIn message or meeting recorded in the Hub, wherever they are sitting. Not the same as never reached: the mail sync only records sends from the day it was switched on and looks back ten days, so an email sent before that is in Gmail (and on the lead's Emails tab) but not here. A touch ticked by hand or logged from the Hub counts."
              />
            </button>
          )}

          {reasons.length === 0 ? null : (
            <div className="pipeline-losses">
              <p className="dash-group-label">Why we lost them</p>
              <ul>
                {reasons.map((entry) => (
                  <li key={entry.reason}>
                    <button
                      type="button"
                      className={
                        selection.kind === 'reason' && selection.reason === entry.reason
                          ? 'pipeline-loss--on'
                          : undefined
                      }
                      onClick={() => {
                        pick({ kind: 'reason', reason: entry.reason });
                      }}
                    >
                      <span>{entry.reason}</span>
                      <b>{entry.count}</b>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </article>

        <article className="pipeline-card">
          <header className="pipeline-card-head">
            <h3>Actions</h3>
            <p>What the team is doing.</p>
          </header>
          <ul className="pipeline-funnel">
            {actions.lines.map((line) => (
              <li key={line.key}>
                <ActionRow
                  label={line.label}
                  explainer={ACTION_EXPLAINERS[line.key]}
                  count={line.count}
                  unit={line.unit}
                  on={selection.kind === 'action' && selection.action === line.key}
                  onPick={() => {
                    pick({ kind: 'action', action: line.key });
                  }}
                />
              </li>
            ))}
          </ul>

          {/* The two that ask for a decision today, side by side rather than
              in the list above: they are not steps of anything, they are the
              answer to "did we miss something". */}
          <div className="pipeline-chips">
            <Chip
              tone="warn"
              count={actions.dueToday.count}
              label="due today"
              explainer={ATTENTION_EXPLAINERS.dueToday}
              on={selection.kind === 'due'}
              onPick={() => {
                pick({ kind: 'due' });
              }}
            />
            <Chip
              tone="danger"
              count={actions.overdue.count}
              label="overdue"
              explainer={ATTENTION_EXPLAINERS.overdue}
              on={selection.kind === 'overdue'}
              onPick={() => {
                pick({ kind: 'overdue' });
              }}
            />
            {/* Drop C. The three rules that mark a lead as falling through
                the cracks — Alex's words. Same row as due today and overdue,
                because they are the same question: did we miss something.
                A chip reading 0 stays on the page: "nothing is behind" is an
                answer, and a chip that vanishes at zero teaches people the
                row is unreliable. */}
            {attention.map((line) => (
              <Chip
                key={line.key}
                tone="quiet"
                count={line.count}
                label={line.label}
                explainer={ATTENTION_RULE_EXPLAINERS[line.key]}
                on={selection.kind === 'attention' && selection.rule === line.key}
                onPick={() => {
                  pick({ kind: 'attention', rule: line.key });
                }}
              />
            ))}
          </div>

          {actions.people.length === 0 ? null : (
            <div className="pipeline-people">
              <p className="dash-group-label">
                Who did what
                <InfoHint label="Who did what" text={ATTENTION_EXPLAINERS.whoDidWhat} />
              </p>
              <table>
                <thead>
                  <tr>
                    <th aria-hidden="true" />
                    <th>Done</th>
                    <th>Prospecting</th>
                    <th>Planned</th>
                    <th>Overdue</th>
                    <th>Admin</th>
                  </tr>
                </thead>
                <tbody>
                  {actions.people.map((person) => (
                    <tr key={person.personId}>
                      <td>{person.name}</td>
                      <td>{person.done}</td>
                      <td>{person.prospecting}</td>
                      <td>{person.planned}</td>
                      <td className={person.overdue > 0 ? 'pipeline-people-late' : undefined}>
                        {person.overdue}
                      </td>
                      <td>{person.admin}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Kyu raised this himself, and it would otherwise quietly corrupt
              Done. Said on the page rather than hidden in a comment, because a
              proxy nobody knows about is a number people trust too much. */}
          <p className="pipeline-proxy">
            Counts touches on leads still moving, plus the chase on prospects (connection requests
            and messages). Touches on Won or Closed leads read as account management and are left
            out. There is no tag for this, so it is a proxy, and it is said here rather than hidden.
          </p>
        </article>
      </section>

      {/* ── The one panel ─────────────────────────────────────────────────
          It holds whatever was last clicked and REPLACES its contents rather
          than appending. That is the whole reason this page cannot grow back
          into the thing it replaced. */}
      <section className="pipeline-panel">
        {selection.kind === 'origins' ? (
          <>
            <header className="pipeline-panel-head">
              <h3>
                By origin
                <InfoHint
                  label="By origin"
                  text="One row per origin, biggest first. Click a row to narrow the whole page to it. Conversion is WON deals over everyone who arrived from that origin, so a row with open deals and no wins reads 0% — which is the honest number. The bottom row is the whole slice."
                />
              </h3>
              <span>{String(byOrigin.length)} origins in this window</span>
            </header>
            <table className="pipe-origins">
              <thead>
                <tr className="pipe-origins-groups">
                  <th aria-hidden="true" />
                  <th>They arrived</th>
                  <th colSpan={2}>We reached out</th>
                  <th colSpan={4}>What came of it</th>
                </tr>
                <tr>
                  <th>Origin</th>
                  <th>Total</th>
                  <th>Reached out</th>
                  <th>Replied</th>
                  <th>Deals</th>
                  <th>Won</th>
                  <th>Conv.</th>
                  <th>Gone</th>
                </tr>
              </thead>
              <tbody>
                {byOrigin.map((row) => (
                  <OriginLine key={row.key} row={row} onPickOrigin={onPickOrigin} />
                ))}
              </tbody>
              <tfoot>
                <OriginLine row={total} onPickOrigin={null} />
              </tfoot>
            </table>
          </>
        ) : (
          <>
            <header className="pipeline-panel-head">
              <h3>{panel.title}</h3>
              <span>{panel.note}</span>
            </header>
            {panel.items !== undefined ? (
              panel.items.length === 0 ? (
                <p className="tracker-field-note">Nothing planned in this slice.</p>
              ) : (
                <table className="tracker-table pipeline-table pipeline-items">
                  <thead>
                    <tr>
                      <th>Who</th>
                      <th>What</th>
                      <th>Due</th>
                      <th>Owner</th>
                    </tr>
                  </thead>
                  <tbody>
                    {panel.items.map((item) => (
                      <tr key={item.key} className={`pipeline-item pipeline-item--${item.state}`}>
                        <td className="pipeline-who">
                          <span className="pipeline-who-inner">
                            <button
                              type="button"
                              className="pipeline-name"
                              onClick={() => {
                                onOpenLead(item.leadId);
                              }}
                            >
                              {rowById.get(item.leadId)?.name ?? 'This lead'}
                            </button>
                            {(() => {
                              const company = rowById.get(item.leadId)?.company ?? '';
                              return company === '' ? null : <small>{company}</small>;
                            })()}
                          </span>
                        </td>
                        <td>{item.what}</td>
                        <td>
                          {item.dueOn === null
                            ? '—'
                            : item.kind === 'round'
                              ? `ends ${dayLabel(item.dueOn)}`
                              : dayLabel(item.dueOn)}
                        </td>
                        {/* A round belongs to the lead, not to a person. An
                            em dash rather than an invented owner. */}
                        <td>{item.owner === '' ? '—' : item.owner}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            ) : panel.rows.length === 0 ? (
              <p className="tracker-field-note">Nobody in this slice.</p>
            ) : (
              <table className="tracker-table pipeline-table">
                <thead>
                  <tr>
                    <th>Who</th>
                    <th>Arrived</th>
                    <th>Last touch</th>
                    <th>Replied</th>
                  </tr>
                </thead>
                <tbody>
                  {panel.rows.map((row) => (
                    <PipelineRowLine key={row.leadId} row={row} onOpenLead={onOpenLead} />
                  ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </section>
    </div>
  );
}

/**
 * What the panel shows for a selection.
 *
 * Pulled out of the component so the title and the list cannot disagree: one
 * place decides both.
 */
function panelFor(
  selection: Selection,
  funnel: ReturnType<typeof buildFunnel>,
  never: PipelineRow[],
  arrived: number,
  actions: ReturnType<typeof buildActions>,
  attention: ReturnType<typeof buildAttention>,
  rowById: ReadonlyMap<string, PipelineRow>,
): { title: string; note: string; rows: PipelineRow[]; items?: PlannedItem[] } {
  const resolve = (leadIds: readonly string[]): PipelineRow[] =>
    leadIds.flatMap((id) => {
      const found = rowById.get(id);
      return found === undefined ? [] : [found];
    });

  /** The sentence under an item list: how much work, on how many leads. */
  const itemNote = (items: PlannedItem[]): string => {
    const leads = new Set(items.map((item) => item.leadId)).size;
    return `${String(items.length)} ${items.length === 1 ? 'action' : 'actions'} on ${String(leads)} ${leads === 1 ? 'lead' : 'leads'}`;
  };

  if (selection.kind === 'action' && selection.action === 'planned') {
    // The one that has to list the WORK, not the leads. Kyu, 7 September:
    // "when we open it it should also show all the other things to do so we
    // dont have to guess."
    return { title: 'Planned', note: itemNote(actions.planned), rows: [], items: actions.planned };
  }
  if (selection.kind === 'action' && selection.action === 'admin') {
    // Deals, not leads: the rows are the deals' contacts where there is one,
    // and the note says how many deals the count is on.
    const line = actions.lines.find((entry) => entry.key === 'admin');
    const rows = resolve(line?.leadIds ?? []);
    const deals = line?.dealIds?.length ?? 0;
    return {
      title: 'Admin',
      note:
        line === undefined
          ? ''
          : `${String(line.count)} HubSpot ${line.count === 1 ? 'update' : 'updates'} on ${String(deals)} ${deals === 1 ? 'deal' : 'deals'}`,
      rows,
    };
  }
  if (selection.kind === 'action') {
    const line = actions.lines.find((entry) => entry.key === selection.action);
    const rows = resolve(line?.leadIds ?? []);
    return {
      title: line?.label ?? 'Done',
      // The count and the row total are different numbers on purpose: 412
      // touches happened on 96 leads. Printing only one of them is how
      // somebody concludes the list is short.
      // Prospects are not leads and cannot be rows here; the note carries
      // that part of the count so the list never looks short.
      note:
        line === undefined
          ? ''
          : `${String(line.count)} ${line.unit} on ${String(rows.length)} ${rows.length === 1 ? 'lead' : 'leads'}${
              line.prospecting.count === 0
                ? ''
                : `, ${String(line.prospecting.count)} of them on ${String(line.prospecting.prospectIds.length)} ${line.prospecting.prospectIds.length === 1 ? 'prospect' : 'prospects'} (see the Prospecting tab)`
            }`,
      rows,
    };
  }
  if (selection.kind === 'attention') {
    const line = attention.find((entry) => entry.key === selection.rule);
    const rows = resolve(line?.leadIds ?? []);
    const label = line?.label ?? '';
    return {
      title: label.charAt(0).toUpperCase() + label.slice(1),
      note: `${String(rows.length)} ${rows.length === 1 ? 'lead needs' : 'leads need'} a look`,
      rows,
    };
  }
  if (selection.kind === 'due') {
    const items = actions.planned.filter((item) => item.state === 'today');
    return { title: 'Due today', note: itemNote(items), rows: [], items };
  }
  if (selection.kind === 'overdue') {
    const items = actions.planned.filter((item) => item.state === 'overdue');
    return { title: 'Overdue', note: itemNote(items), rows: [], items };
  }
  if (selection.kind === 'never') {
    return {
      title: 'No outreach logged',
      note: `${String(never.length)} of ${String(arrived)}`,
      rows: never,
    };
  }
  if (selection.kind === 'reason') {
    const rows = (funnel.find((line) => line.key === 'lost')?.rows ?? []).filter(
      (row) => row.closedBecause.trim() === selection.reason,
    );
    return {
      title: selection.reason,
      note: `${String(rows.length)} lost for this reason`,
      rows,
    };
  }
  if (selection.kind === 'origins') {
    // Unreachable — the caller branches on 'origins' before asking. Kept
    // total rather than cast so this function stays exhaustive.
    return { title: 'By origin', note: '', rows: [] };
  }
  const line = funnel.find((entry) => entry.key === selection.step);
  return {
    title: line?.label ?? 'Arrived',
    note:
      line === undefined
        ? ''
        : `${String(line.count)} of ${String(arrived)} · ${String(sharePercent(line.count, arrived))}%`,
    rows: line?.rows ?? [],
  };
}

/**
 * One row of the actions funnel.
 *
 * Deliberately NOT the outcome funnel's row. These are not steps of one
 * cohort and they have no shared base, so a bar and a percentage would invite
 * somebody to read Done over Planned as a completion rate — which it is not:
 * Planned is a now-number and Done is a window. The unit word does that job
 * instead, and says what the number is made of.
 */
function ActionRow({
  label,
  explainer,
  count,
  unit,
  on,
  onPick,
}: {
  label: string;
  explainer: string;
  count: number;
  unit: string;
  on: boolean;
  onPick: () => void;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={on}
      className={`pipeline-action${on ? ' pipeline-action--on' : ''}`}
      onClick={onPick}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onPick();
        }
      }}
    >
      <span className="pipeline-step-label">
        {label}
        {/* The row is a button and this sits inside it, so before drop 142 a
            click on the ⓘ bubbled up and merely changed the selection. Now it
            would leave the page for the Leads tab, taking the definition the
            person opened it to read with it. Stop here. */}
        <span
          onClick={(event) => {
            event.stopPropagation();
          }}
          onKeyDown={(event) => {
            event.stopPropagation();
          }}
        >
          <InfoHint label={label} text={explainer} />
        </span>
      </span>
      <b className="pipeline-step-count">{count}</b>
      <span className="pipeline-action-unit">{unit}</span>
    </div>
  );
}

/** One attention chip: a number, a word, and an explainer. */
function Chip({
  tone,
  count,
  label,
  explainer,
  on,
  onPick,
}: {
  tone: 'warn' | 'danger' | 'quiet';
  count: number;
  label: string;
  explainer: string;
  on: boolean;
  onPick: () => void;
}) {
  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={on}
      className={`pipeline-chip pipeline-chip--${tone}${on ? ' pipeline-chip--on' : ''}`}
      onClick={onPick}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onPick();
        }
      }}
    >
      <b>{count}</b>
      <span>{label}</span>
      <InfoHint label={label} text={explainer} />
    </div>
  );
}

/** The same segmented switch the Dashboard uses, reusing its classes. */
function RangeSwitch({
  range,
  onChange,
}: {
  range: PipelineRangeKey;
  onChange: (key: PipelineRangeKey) => void;
}) {
  return (
    <div className="dash-range-switch" role="group" aria-label="Arrived">
      {PIPELINE_RANGE_ORDER.map((key) => (
        <button
          key={key}
          type="button"
          className={`dash-range-option${range === key ? ' dash-range-option--on' : ''}`}
          aria-pressed={range === key}
          onClick={() => {
            onChange(key);
          }}
        >
          {PIPELINE_RANGE_LABELS[key]}
        </button>
      ))}
    </div>
  );
}

/**
 * One step of the funnel.
 *
 * A div with a button role rather than a <button>, because the ⓘ inside it is
 * itself a button and a button cannot nest inside a button. Keyboard handling
 * is written out for the same reason.
 */
function PipelineRowLine({
  row,
  onOpenLead,
}: {
  row: PipelineRow;
  onOpenLead: (leadId: string) => void;
}) {
  // Two marks, both on the row's leading edge so they never compete with the
  // text: green for a reply, amber for somebody nobody has contacted.
  const mark = row.replied
    ? ' pipeline-row--replied'
    : row.contacted
      ? ''
      : ' pipeline-row--waiting';
  return (
    <tr className={`pipeline-row${mark}`}>
      <td className="pipeline-who">
        <span className="pipeline-who-inner">
          <button
            type="button"
            className="pipeline-name"
            onClick={() => {
              onOpenLead(row.leadId);
            }}
          >
            {row.name}
          </button>
          {row.company === '' ? null : <small>{row.company}</small>}
          {/* Kyu: "did anyone say not interested or fell off?" The database
              refuses to close a lead without a reason, so this is always
              there on a closed row — the band was just hiding it. */}
          {row.closedBecause === '' ? null : (
            <span className="pipeline-reason">{row.closedBecause}</span>
          )}
        </span>
      </td>
      <td>{dayLabel(row.arrivedOn)}</td>
      <td>{row.lastTouchOn === null ? '—' : dayLabel(row.lastTouchOn)}</td>
      <td>
        {row.replied ? (
          <Badge tone="success" micro>
            Replied
          </Badge>
        ) : (
          '—'
        )}
      </td>
    </tr>
  );
}

/**
 * One origin's row.
 *
 * The whole row is the click target rather than the label alone: an
 * eight-column row with one live word in it is a row people miss.
 */
function OriginLine({
  row,
  onPickOrigin,
}: {
  row: OriginRow;
  /** Null on the total row: it is every origin, so there is nothing to narrow to. */
  onPickOrigin: ((group: string | null, originId: string | null) => void) | null;
}) {
  // "No origin recorded" has nothing to filter BY — it is the absence of an
  // origin, and the Flags menu already carries a control for exactly that.
  const clickable = row.group !== null && onPickOrigin !== null;
  return (
    <tr
      className={`pipe-origin-row${clickable ? ' pipe-origin-row--pick' : ''}${
        row.group === null && row.originId === null ? ' pipe-origin-row--untagged' : ''
      }`}
      onClick={
        clickable && onPickOrigin !== null
          ? () => {
              onPickOrigin(row.group, row.originId);
            }
          : undefined
      }
    >
      <td className="pipe-origin-name">{row.label}</td>
      <td>{row.arrived}</td>
      <td>{row.emailed}</td>
      <td>{row.replied}</td>
      <td>{row.deals}</td>
      <td>{row.won}</td>
      <td>{row.conversion}%</td>
      <td>{row.gone}</td>
    </tr>
  );
}
